// SEO Audit — pure JS audit that scans the site's dynamic content
// (services, cities, blog posts, plus key static pages) and returns
// per-page scores + actionable issues. Runs entirely client-side; no
// network calls required. Used by the admin dashboard.

import { SERVICES } from "@/data/services";
import { PRIORITY_CITY_DATA } from "@/data/cityContent";
import { ALL_CITY_SLUGS, slugToCity } from "@/data/serviceAreas";
import { BLOG_POSTS } from "@/data/blog";

// --- Tuning knobs ---------------------------------------------------------
const TITLE_MIN = 30;
const TITLE_IDEAL_MIN = 50;
const TITLE_IDEAL_MAX = 60;
const TITLE_MAX = 70;

const DESC_MIN = 120;
const DESC_IDEAL_MIN = 140;
const DESC_IDEAL_MAX = 160;
const DESC_MAX = 170;

const CONTENT_MIN_CHARS = 1200;
const CONTENT_GOOD_CHARS = 2000;

// --- Static pages (key landing pages with hand-coded SEO in JSX) ---------
// Mirror what those pages declare so the audit reflects production behavior.
const STATIC_PAGES = [
  {
    path: "/",
    pageType: "home",
    title:
      "Austin TX General Contractor, Roofing & Maintenance | Black Ridge Construction",
    description:
      "Black Ridge Construction is a full-service Austin general contractor. Roofing, remodeling, additions, repairs, outdoor living, and ongoing home maintenance memberships.",
    h1: "Austin's Full-Service Construction, Roofing & Home Maintenance Partner",
    contentChars: 5200, // approximate sum across home sections
    hasImage: true,
    hasFaqs: true,
    hasSchema: true,
  },
  {
    path: "/maintenance-plans",
    pageType: "static",
    title: "Home Maintenance Plans | Black Ridge Construction Austin TX",
    description:
      "Compare residential HomeCare and commercial PropertyCare maintenance memberships for Austin homes and rental portfolios. Defined covered scopes, member pricing, priority scheduling.",
    h1: "Home Maintenance Plans",
    contentChars: 4500,
    hasImage: true,
    hasFaqs: true,
    hasSchema: true,
  },
  {
    path: "/roofing",
    pageType: "static",
    title: "Austin TX Roofing Contractor | Black Ridge Construction",
    description:
      "Roof inspection, repair, replacement, and storm follow-up across Austin and Central Texas. Honest documentation, no insurance-outcome promises, fair pricing.",
    h1: "Austin Roofing — Inspections, Repairs, and Replacements",
    contentChars: 3600,
    hasImage: true,
    hasFaqs: true,
    hasSchema: true,
  },
  {
    path: "/remodeling",
    pageType: "static",
    title: "Austin TX Remodeling Contractor | Black Ridge Construction",
    description:
      "Kitchen, bath, full-home, and finish-work remodeling across Austin and Central Texas. One general contractor coordinating every trade — clear scope and clear pricing.",
    h1: "Austin Remodeling — Kitchens, Baths, Full-Home, and More",
    contentChars: 3400,
    hasImage: true,
    hasFaqs: true,
    hasSchema: true,
  },
  {
    path: "/construction-services",
    pageType: "static",
    title: "Construction Services in Austin, TX | Black Ridge Construction",
    description:
      "Complete list of construction services Black Ridge provides across Austin and Central Texas: roofing, remodeling, additions, exterior, outdoor living, repairs, and maintenance.",
    h1: "All Construction Services",
    contentChars: 2200,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/service-areas",
    pageType: "static",
    title: "Service Areas Across Austin & Central Texas | Black Ridge",
    description:
      "Service areas covered by Black Ridge Construction across Austin, Round Rock, Cedar Park, Georgetown, Pflugerville, Kyle, Buda, Lakeway, Dripping Springs, and more.",
    h1: "Service Areas — Austin & Central Texas",
    contentChars: 2400,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/popular-austin-services",
    pageType: "static",
    title: "Popular Austin Home Services | Black Ridge Construction",
    description:
      "Trending Austin home upgrades — covered patios, outdoor kitchens, kitchen and bath upgrades, ADU and garage conversions, and roof replacements.",
    h1: "Popular Austin-Area Projects",
    contentChars: 1900,
    hasImage: true,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/about",
    pageType: "static",
    title: "About Black Ridge Construction | Austin TX General Contractor",
    description:
      "About Black Ridge Construction — our philosophy, service model, and why Austin homeowners choose a real general contractor instead of trade-by-trade hires.",
    h1: "About Black Ridge Construction",
    contentChars: 2100,
    hasImage: true,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/testimonials",
    pageType: "static",
    title:
      "Client Testimonials & Project Stories | Black Ridge Construction Austin TX",
    description:
      "50 homeowner testimonials and project stories across Austin, Round Rock, Cedar Park, Georgetown, Kyle, Buda, and Central Texas. 4.7/5 average rating.",
    h1: "What Austin & Central Texas Homeowners Are Saying",
    contentChars: 5000,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/contact",
    pageType: "static",
    title: "Contact Black Ridge Construction | Austin TX",
    description:
      "Request a consultation, ask about roofing or remodeling, or get on the maintenance membership list. Serving Austin and Central Texas.",
    h1: "Contact Black Ridge Construction",
    contentChars: 900,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/subcontractor-partnerships",
    pageType: "static",
    title: "Subcontractor Partnerships | Black Ridge Construction Austin",
    description:
      "Apply to join our network of trade partners for residential construction, roofing, remodeling, and maintenance work across the Austin area.",
    h1: "Subcontractor Partnerships",
    contentChars: 1400,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
  {
    path: "/blog",
    pageType: "static",
    title: "Resources & Blog | Black Ridge Construction",
    description:
      "Resources and articles on roofing, remodeling, maintenance, and home care for Austin and Central Texas homeowners.",
    h1: "Resources / Blog",
    contentChars: 800,
    hasImage: false,
    hasFaqs: false,
    hasSchema: true,
  },
];

// --- Helpers --------------------------------------------------------------
function lenOrZero(s) {
  if (!s || typeof s !== "string") return 0;
  return s.length;
}

function sumArrayLen(value, mapFn = lenOrZero) {
  if (!Array.isArray(value)) return 0;
  return value.reduce((a, item) => a + mapFn(item), 0);
}

function scoreTitle(title) {
  const len = lenOrZero(title);
  if (len === 0) return { score: 0, severity: "critical", message: "Missing meta title" };
  if (len < TITLE_MIN) return { score: 40, severity: "warn", message: `Title too short (${len} chars; aim 50-60)` };
  if (len > TITLE_MAX) return { score: 60, severity: "warn", message: `Title too long (${len} chars; aim 50-60, may truncate in SERPs)` };
  if (len < TITLE_IDEAL_MIN || len > TITLE_IDEAL_MAX) {
    return { score: 85, severity: "ok", message: `Title OK (${len} chars; ideal 50-60)` };
  }
  return { score: 100, severity: "pass", message: `Title length ideal (${len} chars)` };
}

function scoreDescription(desc) {
  const len = lenOrZero(desc);
  if (len === 0) return { score: 0, severity: "critical", message: "Missing meta description" };
  if (len < DESC_MIN) return { score: 50, severity: "warn", message: `Description too short (${len} chars; aim 140-160)` };
  if (len > DESC_MAX) return { score: 65, severity: "warn", message: `Description too long (${len} chars; aim 140-160, may truncate)` };
  if (len < DESC_IDEAL_MIN || len > DESC_IDEAL_MAX) {
    return { score: 85, severity: "ok", message: `Description OK (${len} chars; ideal 140-160)` };
  }
  return { score: 100, severity: "pass", message: `Description length ideal (${len} chars)` };
}

function scoreH1(h1) {
  if (!h1) return { score: 0, severity: "critical", message: "Missing H1 / headline" };
  return { score: 100, severity: "pass", message: "H1 present" };
}

function scoreContent(chars) {
  if (!chars || chars < 300) return { score: 20, severity: "critical", message: `Thin content (${chars} chars; aim 1200+)` };
  if (chars < CONTENT_MIN_CHARS) return { score: 65, severity: "warn", message: `Below recommended depth (${chars} chars; aim 1200+)` };
  if (chars < CONTENT_GOOD_CHARS) return { score: 88, severity: "ok", message: `Good depth (${chars} chars)` };
  return { score: 100, severity: "pass", message: `Strong depth (${chars} chars)` };
}

function scoreBoolean(value, label) {
  return value
    ? { score: 100, severity: "pass", message: `${label} present` }
    : { score: 60, severity: "warn", message: `${label} missing` };
}

// Pure function: takes the normalized page record and returns a graded report
function gradePage(page) {
  const checks = {
    title: scoreTitle(page.title),
    description: scoreDescription(page.description),
    h1: scoreH1(page.h1),
    content: scoreContent(page.contentChars),
    image: scoreBoolean(page.hasImage, "Hero image"),
    faqs: scoreBoolean(page.hasFaqs, "FAQ section"),
    schema: scoreBoolean(page.hasSchema, "JSON-LD schema"),
  };
  // Weighted: title + description + content carry more weight in local SEO
  const weights = { title: 2, description: 2, h1: 1, content: 2, image: 1, faqs: 1, schema: 1 };
  let weightedSum = 0;
  let totalWeight = 0;
  Object.entries(checks).forEach(([k, c]) => {
    weightedSum += c.score * weights[k];
    totalWeight += weights[k];
  });
  const score = Math.round(weightedSum / totalWeight);

  const issues = Object.entries(checks)
    .filter(([, c]) => c.severity !== "pass")
    .map(([k, c]) => ({ key: k, severity: c.severity, message: c.message }));

  let band = "excellent";
  if (score < 50) band = "critical";
  else if (score < 70) band = "needs-work";
  else if (score < 90) band = "good";

  return { ...page, score, band, checks, issues };
}

// --- Page list builders ---------------------------------------------------
function estimateServiceContentChars(svc) {
  const intro = lenOrZero(svc?.intro);
  const overview = sumArrayLen(svc?.overview);
  const problems = sumArrayLen(svc?.problemsSolved);
  const process = sumArrayLen(svc?.process);
  const faqs = sumArrayLen(svc?.faqs, (f) => lenOrZero(f?.q) + lenOrZero(f?.a));
  return intro + overview + problems + process + faqs + 600; // shared template adds ~600 chars
}

function buildServicePages() {
  return Object.entries(SERVICES).map(([slug, svc]) => ({
    path: `/services/${slug}`,
    pageType: "service",
    title: svc.metaTitle || svc.title,
    description: svc.metaDescription || "",
    h1: svc.hero || svc.title,
    contentChars: estimateServiceContentChars(svc),
    hasImage: Boolean(svc.image),
    hasFaqs: Array.isArray(svc.faqs) && svc.faqs.length > 0,
    hasSchema: true,
  }));
}

function buildCityPages() {
  const priorityPages = Object.entries(PRIORITY_CITY_DATA).map(([slug, city]) => {
    const contentChars =
      lenOrZero(city?.intro) + lenOrZero(city?.localContext) +
      sumArrayLen(city?.commonProjects) +
      1200; // shared sections (pillars, quick links, FAQ) add ~1200 chars
    return {
      path: `/service-areas/${slug}-tx`,
      pageType: "city-priority",
      title: city.metaTitle || `${slugToCity(slug)} TX General Contractor | Black Ridge Construction`,
      description: city.metaDescription || "",
      h1: city.headline || `${slugToCity(slug)} Construction, Roofing & Home Maintenance`,
      contentChars,
      hasImage: false,
      hasFaqs: true, // city pages always render auto-generated FAQs
      hasSchema: true,
    };
  });

  const prioritySet = new Set(Object.keys(PRIORITY_CITY_DATA));
  const fallbackPages = ALL_CITY_SLUGS
    .filter((s) => !prioritySet.has(s))
    .map((slug) => ({
      path: `/service-areas/${slug}-tx`,
      pageType: "city-fallback",
      // mirror fallback meta computed at runtime in CityDetail.jsx
      title: `${slugToCity(slug)} TX General Contractor | Black Ridge Construction`,
      description: `Black Ridge Construction serves ${slugToCity(slug)}, TX with roofing, remodeling, additions, exterior repair, and ongoing maintenance plans.`,
      h1: `${slugToCity(slug)} Construction, Roofing & Home Maintenance`,
      contentChars: 1300, // shared fallback content
      hasImage: false,
      hasFaqs: true,
      hasSchema: true,
    }));

  return [...priorityPages, ...fallbackPages];
}

function buildBlogPages() {
  return BLOG_POSTS.map((post) => {
    const sectionsChars = sumArrayLen(post.sections, (s) => {
      const body = sumArrayLen(s?.body);
      const list = sumArrayLen(s?.list);
      return lenOrZero(s?.h2) + body + list;
    });
    const faqChars = sumArrayLen(post.faqs, (f) => lenOrZero(f?.q) + lenOrZero(f?.a));
    return {
      path: `/blog/${post.slug}`,
      pageType: "blog",
      title: post.metaTitle || post.title,
      description: post.metaDescription || post.excerpt || "",
      h1: post.title,
      contentChars: lenOrZero(post.excerpt) + sectionsChars + faqChars,
      hasImage: Boolean(post.image),
      hasFaqs: Array.isArray(post.faqs) && post.faqs.length > 0,
      hasSchema: true,
    };
  });
}

// --- Public API -----------------------------------------------------------
export function runSeoAudit() {
  const pages = [
    ...STATIC_PAGES,
    ...buildServicePages(),
    ...buildCityPages(),
    ...buildBlogPages(),
  ].map(gradePage);

  const total = pages.length;
  const avg = Math.round(pages.reduce((a, p) => a + p.score, 0) / Math.max(total, 1));

  const byBand = pages.reduce(
    (acc, p) => {
      acc[p.band] = (acc[p.band] || 0) + 1;
      return acc;
    },
    { excellent: 0, good: 0, "needs-work": 0, critical: 0 },
  );

  const byType = pages.reduce((acc, p) => {
    if (!acc[p.pageType]) acc[p.pageType] = { count: 0, totalScore: 0 };
    acc[p.pageType].count += 1;
    acc[p.pageType].totalScore += p.score;
    return acc;
  }, {});
  const typeBreakdown = Object.entries(byType).map(([type, v]) => ({
    type,
    count: v.count,
    avgScore: Math.round(v.totalScore / v.count),
  }));

  // Aggregate issue categories
  const issueCounts = {};
  pages.forEach((p) => {
    p.issues.forEach((i) => {
      issueCounts[i.key] = (issueCounts[i.key] || 0) + 1;
    });
  });
  const topIssues = Object.entries(issueCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({ key, count }));

  return {
    overallScore: avg,
    totalPages: total,
    byBand,
    typeBreakdown,
    topIssues,
    pages: pages.sort((a, b) => a.score - b.score), // worst-first
  };
}

export const BAND_LABELS = {
  excellent: { label: "Excellent", color: "bg-emerald-500" },
  good: { label: "Good", color: "bg-amber-500" },
  "needs-work": { label: "Needs Work", color: "bg-orange-500" },
  critical: { label: "Critical", color: "bg-red-500" },
};
